import { createContext, SetStateAction, Dispatch, MutableRefObject } from 'react';

export enum AIRolePlayStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

export enum Role {
  AI = 'ai',
  LEARNER = 'learner',
}

export enum MessageStatus {
  PENDING = 'pending',
  SENT = 'sent',
  FAILED = 'failed',
}
export enum AudioRecordingStatus {
  NOT_STARTED = 'not_started',
  RECORDING = 'recording',
  TRANSCRIPTING = 'transcripting',
}

export type Message = {
  id?: string;
  content: string;
  role: Role;
  createdAt?: Date;
  status?: MessageStatus;
};

export type SessionData = {
  aiApiEndpoint: string | null;
  jwtToken: string | null;
  submissionId: number | null;
  expiresAt: string | null;
  languageLocale: string | null;
};

export type AiRolePlaySubmissionPusherData = {
  userId: number;
  courseId: number;
  lecturePageId: number;
  aiRolePlayId: number;
  aiRolePlaySubmissionId: number;
  pointsReceived: number;
  finishedAt: string;
  leaderboardPoints: number;
  leaderboardRank: number;
  priorLeaderboardRank: number;
  closingReason?: string;
};
type AiConversationContextType = {
  aiRolePlayStatus: AIRolePlayStatus;
  setAiRolePlayStatus: (value: AIRolePlayStatus) => void;
  showScenarioDetails: boolean;
  setShowScenarioDetails: (value: boolean) => void;
  /**
   * Exits the chat. If skipConfirmation is true, exits immediately without
   * showing the confirmation dialog (used for timeout scenarios).
   */
  handleExitChat: (skipConfirmation?: boolean) => void;
  aiRolePlayId: number | null;
  messages: Message[];
  setMessages: Dispatch<SetStateAction<Message[]>>;
  sessionData: SessionData;
  setSessionData: Dispatch<SetStateAction<SessionData>>;
  isTimeout: boolean;
  setIsTimeout: Dispatch<SetStateAction<boolean>>;
  setShowConversationModal: Dispatch<SetStateAction<boolean>>;
  /**
   * Ref to hold the AbortController for the current stream.
   * Used to abort the stream when exiting the chat.
   */
  abortControllerRef: MutableRefObject<AbortController | null>;
  isHardDeadlineExpired: boolean;
  isRolePlayStarting: boolean;
  setIsRolePlayStarting: Dispatch<SetStateAction<boolean>>;
  // Conversation handlers
  openingNarrative: string;
  handleChunk: (chunk: any) => void;
  handleError: () => void;
  handleStartConversation: () => void;
  /**
   * True while we are waiting for AI tokens to start arriving after the
   * opening narrative `done` event but before the first `delta` of the
   * first AI message. Used by the chat panel to render the typing UI
   * during this gap (the same UI shown while waiting on a learner-triggered reply).
   */
  isAwaitingAiResponse: boolean;
  audioRecordingStatus?: AudioRecordingStatus;
  setAudioRecordingStatus?: Dispatch<SetStateAction<AudioRecordingStatus>>;
};

const AiConversationContext = createContext<AiConversationContextType>({
  aiRolePlayStatus: AIRolePlayStatus.NOT_STARTED,
  setAiRolePlayStatus: () => {},
  showScenarioDetails: true,
  setShowScenarioDetails: () => {},
  handleExitChat: () => {},
  aiRolePlayId: null,
  messages: [],
  setMessages: () => {},
  sessionData: {
    aiApiEndpoint: null,
    jwtToken: null,
    submissionId: null,
    expiresAt: null,
    languageLocale: null,
  },
  setSessionData: () => {},
  isTimeout: false,
  setIsTimeout: () => {},
  setShowConversationModal: () => {},
  abortControllerRef: { current: null },
  isHardDeadlineExpired: false,
  isRolePlayStarting: false,
  setIsRolePlayStarting: () => {},
  // Conversation handlers defaults
  openingNarrative: '',
  handleChunk: () => {},
  handleError: () => {},
  handleStartConversation: () => {},
  isAwaitingAiResponse: false,
  audioRecordingStatus: AudioRecordingStatus.NOT_STARTED,
  setAudioRecordingStatus: () => {},
});

export default AiConversationContext;
